import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCookie, parseConsent, CONSENT_VERSION } from '../auth/PrivacyBanner';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../utils/supabase';
import '../pages/StaticPages.css';

const Cookies = () => {
  const [busy, setBusy] = useState(false);
  const [necessaryOn, setNecessaryOn] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const initFromCookie = () => {
    const raw = getCookie('privacy_consent');
    const parsed = parseConsent(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) {
      setNecessaryOn(false);
      setAnalyticsOn(false);
      return;
    }
    setNecessaryOn(!!parsed.necessary);
    setAnalyticsOn(!!parsed.analytics);
  };

  useEffect(() => { initFromCookie(); }, []);

  const postConsent = async (necessary, analytics) => {
    try {
      setBusy(true);
      await fetch('/api/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ necessary, analytics })
      });
      // Optimistically reflect state and refresh cookie state
      setNecessaryOn(!!necessary);
      setAnalyticsOn(!!analytics);
      // If authenticated, persist consent to Supabase under RLS using user's JWT
      if (user) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            await fetch('/api/consent/persist', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ version: CONSENT_VERSION, necessary, analytics })
            });
          }
        } catch (_) {}
      }
      // If necessary is turned off and the user is authenticated, sign out and redirect to login
      if (!necessary && user) {
        try { await supabase.auth.signOut(); } catch (_) {}
        navigate('/login');
        return;
      }
      // Otherwise reload to propagate globally (e.g., analytics init/teardown)
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-root">
      <Link to="/" className="back-link">← Back</Link>
      <div className="content-card">
        <h1 className="page-title">Cookies</h1>
        <div className="content-body">
          <h2>COOKIE POLICY</h2>
          <p><strong>Last updated:</strong> 4 September 2025</p>
          
          <p>This Cookie Policy explains how Looway Community Interest Company ("Company," "we," "us," and "our") uses cookies and similar technologies to recognize you when you visit our website at <a href="https://www.looway.io" target="_blank" rel="noopener noreferrer">https://www.looway.io</a> ("Website"). It explains what these technologies are and why we use them, as well as your rights to control our use of them.</p>
          
          <p>Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.</p>
          
          <p>Cookies set by the website owner (in this case, Looway Community Interest Company) are called "first-party cookies." Cookies set by parties other than the website owner are called "third-party cookies." Third-party cookies enable third-party features or functionality to be provided on or through the website (e.g. interactive content and analytics). The parties that set these third-party cookies can recognize your computer both when it visits the website in question and also when it visits certain other websites.</p>

          <h2>Why do we use cookies?</h2>
          <p>We use first- and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Online Properties. Third parties serve cookies through our Website for analytics, and other purposes. This is described in more detail below.</p>

          <h2>How can I control cookies?</h2>
          <p>You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the Cookie Consent Management section below (the 'Cookie Consent Manager'). The Cookie Consent Manager allows you to select which categories of cookies you accept or reject. Essential cookies cannot be rejected as they are strictly necessary to provide you with services.</p>
                    
          <p>The specific types of first- and third-party cookies served through our Website and the purposes they perform are described in the table below (please note that the specific cookies served may vary depending on the specific Online Properties you visit).</p>

          <h2>How can I control cookies on my browser?</h2>
          <p>The means by which you can refuse cookies through your web browser controls vary from browser to browser. You should visit your browser's help menu for more information. In addition, most advertising networks offer you a way to opt out of targeted advertising. If you would like to find out more information, please visit:</p>
          <ul>
            <li>Digital Advertising Alliance</li>
            <li>Digital Advertising Alliance of Canada</li>
            <li>European Interactive Digital Advertising Alliance</li>
          </ul>

          <h2>How often will you update this Cookie Policy?</h2>
          <p>We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal, or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed about our use of cookies and related technologies. The date at the top of this Cookie Policy indicates when it was last updated.</p>

          <h2>Where can I get further information?</h2>
          <p>If you have any questions about our use of cookies or other technologies, please contact us at <a href="https://www.looway.io/contact" target="_blank" rel="noopener noreferrer">https://www.looway.io/contact</a>.</p>

          <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid #ddd' }} />

          <h2>Cookie Consent Management</h2>
          <p>
            We use necessary cookies for core functionality, including authentication via Supabase and the provision of Google Maps services, which may require Google to set cookies for geocoding and map features. We also use
            optional analytics through PostHog to understand usage and improve the product.
          </p>
          
          <h3>Consent</h3>
          <p>
            Your consent is stored in a cookie named <code>privacy_consent</code> and includes the version and your
            preferences for necessary and analytics cookies. The consent cookie expires after 90 days, after which
            we will ask you to confirm your choices again. You can update your choice at any time from the
            privacy banner or by clearing the consent cookie.
          </p>

          <h3 style={{ marginTop: 24 }}>Managing cookies</h3>
          <ul>
            <li>Necessary: required for sign‑in and basic features (cannot be disabled) such as the display and interaction with maps provided by Google Maps.</li>
            <li>Analytics: optional and used to improve the product.</li>
          </ul>
          
          <h3>Manage your preferences</h3>
          <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
            {/* Necessary slider (user can disable; note this will prevent sign-in) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', width: '100%' }}>
              <div style={{ flex: '1 1 auto', minWidth: 220 }}>
                <div style={{ fontWeight: 600, color: '#004C10' }}>Necessary</div>
                <div style={{ fontSize: 13, color: '#555' }}>Required for sign‑in and maps. Disabling will turn off sign‑in.</div>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  const nextNecessary = !necessaryOn;
                  await postConsent(nextNecessary, analyticsOn);
                }}
                aria-label="Necessary cookies"
                role="switch"
                aria-checked={necessaryOn}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: necessaryOn ? 0.9 : 1,
                  marginLeft: 'auto'
                }}
              >
                <div style={{ width: 52, height: 28, background: necessaryOn ? '#28a745' : '#d1d5db', borderRadius: 999, position: 'relative', transition: 'background .2s' }}>
                  <div style={{ width: 22, height: 22, background: '#fff', borderRadius: '50%', position: 'absolute', top: 3, left: necessaryOn ? 27 : 3, boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left .2s' }} />
                </div>
              </button>
            </div>

            {/* Analytics slider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', width: '100%' }}>
              <div style={{ flex: '1 1 auto', minWidth: 220 }}>
                <div style={{ fontWeight: 600, color: '#004C10' }}>Analytics</div>
                <div style={{ fontSize: 13, color: '#555' }}>Helps us improve the product</div>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  const next = !analyticsOn;
                  setAnalyticsOn(next);
                  await postConsent(true, next);
                }}
                aria-label="Analytics"
                role="switch"
                aria-checked={analyticsOn}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  marginLeft: 'auto'
                }}
              >
                <div style={{ width: 52, height: 28, background: analyticsOn ? '#28a745' : '#d1d5db', borderRadius: 999, position: 'relative', transition: 'background .2s' }}>
                  <div style={{ width: 22, height: 22, background: '#fff', borderRadius: '50%', position: 'absolute', top: 3, left: analyticsOn ? 27 : 3, boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left .2s' }} />
                </div>
              </button>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Cookies;


